import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import Flag from 'LuxKit/Flag'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LineSyncIcon from 'LuxKit/Icons/line/LineSyncIcon'
import config from 'constants/config'
import Sheet from 'components/utils/Sheet'
import NestableClickable from 'components/Common/NestableClickable'
import { pushWithRegion } from 'actions/NavigationActions'
import useSwitchRegion from 'hooks/useSwitchRegion'

const RootClickable = styled(NestableClickable)`
  &:focus {
    ${interactiveFocusStyle}
  }
`

const MemberText = styled.span`
  text-transform: none;
  font-weight: normal;
  white-space: nowrap;
`

const InlineFlag = styled(Flag)`
  margin-left: ${rem(8)};
  vertical-align: middle;
`

interface Props {
  onLinkClick: () => void
  loyaltyAccountData: App.LoyaltyAccount
}

function AccountMenuLoyaltyHasAccountDetailsTileNonMemberRegion({ onLinkClick, loyaltyAccountData }: Props) {
  const tier = loyaltyAccountData.tier
  const tierName = getLoyaltyTierDisplayName(tier)
  const loyaltyAccountRegion = loyaltyAccountData.region
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const loyaltyCurrency = useMemo(() => {
    return config.regions.find(r => r.code === loyaltyAccountRegion)?.currencyCode
  }, [loyaltyAccountRegion])

  const [switchRegionPath, handleSwitchRegion] = useSwitchRegion(loyaltyAccountRegion)

  const handleOnLinkClick = useCallback(() => {
    dispatch(pushWithRegion(`/${LOYALTY_LANDING_PAGE}`))
    onLinkClick()
  }, [onLinkClick, dispatch])

  const memberText = useMemo(() => {
    return intl.formatMessage(
      {
        id: '-Vz4IhvmupDZz-J61IOr',
        defaultMessage: '· Member {accountNumber}',
        description: 'Member text with account number for loyalty account',
      },
      {
        accountNumber: loyaltyAccountData.accountNumber,
      },
    )
  }, [intl, loyaltyAccountData.accountNumber])

  const accountLinkedMessage = useMemo(() => {
    return intl.formatMessage(
      {
        id: '-la_XSwNlT4r6iHPQQdZ',
        defaultMessage: 'Looks like your account is linked to {currency}',
        description: 'Message indicating account is linked to a different currency region',
      },
      {
        currency: loyaltyCurrency || '',
      },
    )
  }, [intl, loyaltyCurrency])

  const switchRegionDescription = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'iI2vkx8nJ4UXv1puNWjl',
        defaultMessage: 'To earn points and redeem your benefits you’ll need to switch to the {currency} site.',
        description: 'Description explaining need to switch to different currency site',
      },
      {
        currency: loyaltyCurrency || '',
      },
    )
  }, [intl, loyaltyCurrency])

  const switchRegionLinkText = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'DV_LGGcC8XMP12Sju6O1',
        defaultMessage: 'Switch to {currency} site',
        description: 'Hyperlink text to switch to different currency site',
      },
      {
        currency: loyaltyCurrency || '',
      },
    )
  }, [intl, loyaltyCurrency])

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <RootClickable
      onClick={handleOnLinkClick}
    >
      <Sheet
        background={`tier-${tier}-secondary`}
        padding="12 16"
        borderRadius="M"
      >
        <VerticalSpacer gap={20}>
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={4}>
              <Group direction="horizontal" horizontalAlign="space-between">
                <LoyaltyProgramNameLogo width={72} />
                <LoyaltyTierLabel kind="primary" tier={tier} format="uppercase">
                  {tierName}
                  <MemberText>{memberText}</MemberText>
                  {loyaltyAccountRegion && (
                    <InlineFlag size="XS" countryCode={loyaltyAccountRegion} />
                  )}
                </LoyaltyTierLabel>
              </Group>
            </VerticalSpacer>
            <Heading variant="heading4">
              {accountLinkedMessage}
            </Heading>
            <BodyText variant="medium">
              {switchRegionDescription}
            </BodyText>
          </VerticalSpacer>
          <TextLink
            size="medium"
            endIcon={<LineSyncIcon size="XS" />}
            onClick={handleSwitchRegion}
            href={switchRegionPath}
            block
          >
            {switchRegionLinkText}
          </TextLink>
        </VerticalSpacer>
      </Sheet>
    </RootClickable>
  </ProductPaletteProvider>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyHasAccountDetailsTileNonMemberRegion)
